import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { isNumber } from 'lodash-es';

import { AgTrendIndicatorComponent } from '@shared/ag-components/ag-trend-indicator/ag-trend-indicator.component';
import { AgRawTooltipParams } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { Currency } from '@shared/services/gql.service';
import { GlobalState } from '@shared/store/global/global.model';
import { OrganizationModel } from '@shared/store/organization/organization.store';
import { WorkflowModel } from '@shared/store/workflow/workflow.store';
import { decimalDivide, isRoundedToZero } from '@shared/utils';
import { getCellClassForGroups } from '@shared/utils/ag-cell-class-for-groups';
import { Utils } from '@shared/utils/utils';

import { QCGridData } from '../../../../period-close.model';

export type QuarterCloseGridConfigContext = {
  adjustmentWorkflow: null | WorkflowModel;
  attributeConfig: Pick<GlobalState, 'attributes' | 'primaryAttribute' | 'secondaryAttribute'> & {
    gridData: QCGridData[];
  };
  flags: {
    isAPColumnVisible: boolean;
    isContractAmountColumnVisible: boolean;
    isMonthTabAvailable: boolean;
    isNPColumnVisible: boolean;
    isUnpaidColumnsVisible: boolean;
  };
  gridData: QCGridData[];
  isCloseMonthAvailable: boolean;
  isCloseMonthsProcessing: boolean;
  isCurrentQuarterSelected: boolean;
  isWorkflowAvailable: boolean;
  onInvoiceCellClicked: (params: CellClickedEvent<QCGridData>) => void;
  onMonthCloseClicked: () => void;
  onMonthlyWpEstimateCellClicked: (params: ICellRendererParams<QCGridData>) => void;
  openMonth: string;
  organizations: OrganizationModel[];
  permissions: {
    canCloseMonth: boolean;
    canModifyAdjustments: boolean;
  };
  selectedQuarter: string;
  selectedQuarterMonths: string[];
};

export type QuarterCloseMonthColumnConfig = {
  index: number;
  isAfter: boolean;
  isBefore: boolean;
  isCurrent: boolean;
  month: string;
};

export const qcGetMoneyColumn = () => {
  return {
    cellClass: qcGetCurrencyCellClass,
    filter: 'agNumberColumnFilter',
    headerClass: ['text-center'],
    maxWidth: 300,
    valueFormatter: qcCurrencyFormatter,
  } satisfies ColDef;
};

export const qcGetInvoiceColumnDefaults = (ctx: QuarterCloseGridConfigContext) => {
  return {
    cellClass: (p) => [...qcGetCurrencyCellClass(p), ...qcGetCellClassForInvoiceCells(p)],
    onCellClicked: (event: CellClickedEvent<QCGridData>) => {
      if (event.node.group || event.rowPinned === 'bottom') {
        ctx.onInvoiceCellClicked(event);
      }
    },
  } satisfies ColDef;
};

export const qcCurrencyFormatter = (params: ValueFormatterParams<QCGridData>) =>
  Utils.agCurrencyFormatterAccounting(params, params.data?.currency || Currency.USD);

export const qcGetCurrencyCellClass = (params: CellClassParams<QCGridData>) => {
  const currency = params.data?.currency || 'NoSymbol';

  return [`budgetCost${currency}`, 'text-right', ...getCellClassForGroups(params)];
};

export const qcGetCellClassForInvoiceCells = (params: CellClassParams) => {
  if (params.node.group || params.node.rowPinned === 'bottom') {
    return [params.value ? 'aux-link' : 'aux-link-zero-hyphen', 'cursor-pointer'];
  }

  return [];
};

export const qcGetUnpaidInvoicesCellRenderer = () => {
  return {
    cellRenderer: (params: ICellRendererParams) => {
      const eDiv = document.createElement('div');

      eDiv.dataset.pendoId = 'unpaid-invoices-ltd';
      eDiv.innerText = params.valueFormatted || '';

      return eDiv;
    },
    tooltipComponentParams: <AgRawTooltipParams>{
      getNode: ({ targetNode }) => targetNode?.querySelector('div'),
    },
  } satisfies ColDef;
};

export const qcGetFluxPrevValueColumn = (
  field: string,
  headerName: string,
  isOpenMonth: boolean,
  hideByDefault: boolean
): ColDef[] => {
  if (isOpenMonth) {
    return [
      {
        cellClass: (params: CellClassParams) => [...qcGetCurrencyCellClass(params), 'text-right'],
        columnGroupShow: 'open',
        field: field,
        filter: 'agNumberColumnFilter',
        headerClass: ['text-center', 'whitespace-normal'],
        headerName: headerName,
        hide: hideByDefault,
        maxWidth: 300,
        minWidth: 200,
        sortable: true,
        valueFormatter: (params: ValueFormatterParams) => {
          return Utils.currencyFormatter(
            isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
            {},
            params.data.currency
          );
        },
      },
    ];
  }

  return [];
};

export const qcGetFluxAnalysisColumns = (
  fieldNumber: string,
  fieldNumberHeaderName: string,
  fieldPercent: string,
  fieldPercentHeaderName: string,
  isCurrent: boolean,
  hideByDefault: boolean
  // eslint-disable-next-line max-params
): ColDef[] => {
  return isCurrent
    ? [
        {
          cellClass: (params: CellClassParams) => [
            ...qcGetCurrencyCellClass(params),
            'text-right',
            'bg-aux-blue-arctic',
            '!py-0',
          ],
          cellRenderer: AgTrendIndicatorComponent,
          cellRendererParams: (params: ICellRendererParams) => ({
            isIconHidden: !!params.value && isRoundedToZero(params.value),
            oneColorIcons: true,
            textColorClass: params.value < 0 ? 'text-aux-red-dark' : '',
          }),
          columnGroupShow: 'open',
          field: fieldNumber,
          filter: 'agNumberColumnFilter',
          headerClass: ['text-center', 'whitespace-normal', '!bg-aux-blue-arctic'],
          headerName: fieldNumberHeaderName,
          hide: hideByDefault,
          maxWidth: 300,
          minWidth: 200,
          sortable: true,
          valueFormatter: (params: ValueFormatterParams) => {
            return Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency
            );
          },
        },
        {
          cellClass: ['text-right', 'bg-aux-blue-arctic', 'budget-percent', '!py-0'],
          cellRenderer: AgTrendIndicatorComponent,
          cellRendererParams: (params: ICellRendererParams) => ({
            isIconHidden: !!params.value && isRoundedToZero(params.value),
            oneColorIcons: true,
            textColorClass: params.value < 0 ? 'text-aux-red-dark' : '',
          }),
          columnGroupShow: 'open',
          field: fieldPercent,
          filter: 'agNumberColumnFilter',
          headerClass: ['text-center', 'whitespace-normal', '!bg-aux-blue-arctic'],
          headerName: fieldPercentHeaderName,
          hide: hideByDefault,
          minWidth: 200,
          sortable: true,
          valueFormatter: (params: ValueFormatterParams) => {
            return Utils.percentageFormatter(
              isRoundedToZero(params.value) ? 0 : decimalDivide(params.value, 100),
              {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }
            );
          },
        },
      ]
    : [];
};
